import { createStore } from "vuex";
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const toast = useToast();
let fetchCountsPromise = null;
let lastErrorMessage = '';
let lastErrorTime = 0;
const ERROR_COOLDOWN = 5000;

export default createStore({
    state: {
        appMode: process.env.VUE_APP_MODE,
        apiMode: 'default',
        appTitle: 'VECT',
        appLink: process.env.VUE_APP_BACKEND_URL,
        hideConfigButton: false,
        updateAvailable: false,
        updateWaiting: false,
        isOnline: true,
        isPinned: true,
        isSidebarLoaded: false,
        showConfig: false,
        sidebarType: "bg-white",
        mcolor: "",
        darkMode: false,
        isAuthenticated: false,
        isNavFixed: false,
        isAbsolute: false,
        showNavs: true,
        showSidenav: true,
        showNavbar: true,
        showFooter: true,
        showLoading: false,
        loadingStatus: '',
        showMain: true,
        layout: "default",
        user: {},
        acl: [],
        sidebar_navigation: 'regular', // 'regular' or 'settings'
        counts: {
            approvals: 0,
            tickets: 0,
            feedbacks: 0,
            unread: 0
        },
        isCountsLoading: true,
        isAppInitialLoaded: false,
        enable_toaster: false
    },
    mutations: {
        setAppInitialLoaded(state, status) {
            state.isAppInitialLoaded = status;
        },
        setEnableToaster(state, status) {
            state.enable_toaster = status;
        },
        toggleConfigurator(state) {
            state.showConfig = !state.showConfig;
        },
        setApiMode(state, status) {
            state.apiMode = status;
        },
        setApiLink(state, link) {
            state.apiLink = link;
        },
        setSidebar(state, status) {
            state.isPinned = status;
            state.isSidebarLoaded = true;
        },
        sidebarMinimize(state) {
            let sidenav_show = document.querySelector("#app");
            // if (sidenav_show.classList.contains("g-sidenav-pinned") || sidenav_show.classList.contains("g-sidenav-hidden")) {
            let sidenav_classes = ["g-sidenav-hidden", "g-sidenav-pinned"];
            if (sidenav_classes.some(className => sidenav_show.classList.contains(className))) {
                if (state.isPinned) {
                    sidenav_show.classList.add("g-sidenav-hidden");
                    sidenav_show.classList.remove("g-sidenav-pinned");
                    state.isPinned = false;
                } else {
                    sidenav_show.classList.add("g-sidenav-pinned");
                    sidenav_show.classList.remove("g-sidenav-hidden");
                    state.isPinned = true;
                }
            } else {
                sidenav_show.classList.add("g-sidenav-pinned");
                state.isPinned = true;
            }
        },
        sidebarType(state, payload) {
            state.sidebarType = payload;
        },
        navbarFixed(state) {
            if (state.isNavFixed === false) {
                state.isNavFixed = true;
            } else {
                state.isNavFixed = false;
            }
        },
        authenticateUser(state, value) {
            state.isAuthenticated = value ?? true;
        },
        showLoadingMut(state, status = '') {
            state.showLoading = true;
            state.loadingStatus = status;
        },
        hideLoadingMut(state) {
            state.showLoading = false;
        },
        appUpdateStatus(state, status) {
            state.updateAvailable = status;
        },
        setUpdateWaiting(state, status) {
            state.updateWaiting = status;
        },
        updateOnlineStatus(state, status) {
            state.isOnline = status;
        },
        setAppTitle(state, payload) {
            const prefix = state.appMode === 'development' ? 'Test - ' : '';
            state.appTitle = prefix + payload + ' - VECT';
            // Update the document title
            if (state.appMode !== 'production') {
                document.title = state.appTitle;
            } else if (payload) {
                document.title = state.appTitle;
            }
        },
        setAppMode(state, payload) {
            state.appMode = payload;
        },
        userData(state, payload) {
            state.user = payload.user;
            state.acl = payload.acl;
        },
        setSidebarNavigation(state, value) {
            if (['regular', 'settings'].includes(value)) {
                state.sidebar_navigation = value;
            }
        },
        setUserMut(state, user) {
            state.user = user;
        },
        setCounts(state, counts) {
            state.counts = { ...state.counts, ...counts };
        },
        setCountsLoading(state, status) {
            state.isCountsLoading = status;
        },
    },
    actions: {
        toggleSidebarColor({ commit }, payload) {
            commit("sidebarType", payload);
        },
        async fetchCounts({ commit }) {
            if (fetchCountsPromise) return fetchCountsPromise;
            fetchCountsPromise = (async () => {
                commit('setCountsLoading', true);
                try {
                    const { statsService } = await import('@/api');
                    
                    const res = await statsService.getGlobal();
                    const data = res.data.data;

                    const counts = {
                        approvals: data.approvals?.pending || 0,
                        tickets: data.tickets?.active || 0,
                        feedbacks: data.feedbacks?.total_active || data.feedbacks?.pending || 0,
                        unread: data.notifications?.unread ?? (typeof data.notifications === 'number' ? data.notifications : (data.notifications?.count || 0))
                    };

                    commit('setCounts', counts);
                } catch (e) {
                    // Ignore error silently
                } finally {
                    commit('setCountsLoading', false);
                    fetchCountsPromise = null;
                }
            })();
            return fetchCountsPromise;
        },
        triggerToast({ state }, payload) {
            if (!state.enable_toaster) return;

            const now = Date.now();
            if (payload.message === lastErrorMessage && (now - lastErrorTime) < ERROR_COOLDOWN) {
                return;
            }
            lastErrorMessage = payload.message;
            lastErrorTime = now;

            if (typeof window !== 'undefined') {
                toast.open({
                    message: payload.message,
                    type: payload.type || 'error',
                    duration: payload.duration || 5000,
                    position: payload.position || 'top-right',
                });
            }
        }
    },
    getters: {},
});
